/**
 * Error Handling Utilities
 * Provides structured error handling and logging
 *
 * @package
 */

import { __ } from '@wordpress/i18n';

/**
 * Error types for categorisation
 */
export const ErrorTypes = {
	API: 'api',
	VALIDATION: 'validation',
	NETWORK: 'network',
	PARSE: 'parse',
	UNKNOWN: 'unknown',
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
	LOW: 'low',
	MEDIUM: 'medium',
	HIGH: 'high',
	CRITICAL: 'critical',
};

/**
 * Log error with context
 *
 * @param {Error|string} error    Error object or message
 * @param {string}       context  Context where error occurred
 * @param {Object}       metadata Additional metadata
 */
export const logError = ( error, context, metadata = {} ) => {
	const errorMessage = error instanceof Error ? error.message : error;
	const errorStack = error instanceof Error ? error.stack : '';

	// eslint-disable-next-line no-console
	console.error(
		`[${ context }] Error:`,
		errorMessage,
		{
			...metadata,
			stack: errorStack,
			timestamp: new Date().toISOString(),
		}
	);
};

/**
 * Handle API error with user-friendly message
 *
 * @param {Error}  error   Error object
 * @param {string} context Context where error occurred
 * @return {string} User-friendly error message
 */
export const handleApiError = ( error, context ) => {
	logError( error, context, {
		type: ErrorTypes.API,
		severity: ErrorSeverity.MEDIUM,
	} );

	if ( error.message?.includes( '404' ) ) {
		return __( 'The requested item could not be found.', 'at-vehicle-configurator' );
	}

	if ( error.message?.includes( '403' ) || error.message?.includes( '401' ) ) {
		return __( 'You do not have permission to access this resource.', 'at-vehicle-configurator' );
	}

	if ( error.message?.includes( '500' ) ) {
		return __( 'A server error occurred. Please try again later.', 'at-vehicle-configurator' );
	}

	if ( error.message?.includes( 'network' ) || error.message?.includes( 'fetch' ) ) {
		return __( 'Network error. Please check your connection and try again.', 'at-vehicle-configurator' );
	}

	return __( 'An unexpected error occurred. Please try again.', 'at-vehicle-configurator' );
};

/**
 * Handle validation error
 *
 * @param {Object} errors  Validation errors object
 * @param {string} context Context where error occurred
 * @return {Object} Formatted errors object
 */
export const handleValidationError = ( errors, context ) => {
	logError( 'Validation failed', context, {
		type: ErrorTypes.VALIDATION,
		severity: ErrorSeverity.LOW,
		errors,
	} );

	return errors;
};

/**
 * Try-catch wrapper with structured error handling
 *
 * @param {Function} fn      Function to execute
 * @param {string}   context Context description
 * @param {Function} onError Optional error callback
 * @return {Promise} Result or error
 */
export const withErrorHandling = async ( fn, context, onError = null ) => {
	try {
		return await fn();
	} catch ( error ) {
		logError( error, context, {
			type: ErrorTypes.UNKNOWN,
			severity: ErrorSeverity.MEDIUM,
		} );

		if ( onError ) {
			onError( error );
		}

		throw error;
	}
};

/**
 * Create error boundary fallback component
 *
 * @param {Error}  error   Error object
 * @param {string} context Context where error occurred
 * @return {Object} Fallback component props
 */
export const createErrorFallback = ( error, context ) => {
	logError( error, context, {
		type: ErrorTypes.UNKNOWN,
		severity: ErrorSeverity.HIGH,
	} );

	return {
		message: __( 'Something went wrong. Please refresh the page and try again.', 'at-vehicle-configurator' ),
		technicalDetails: error.message,
		showDetails: process.env.NODE_ENV === 'development',
	};
};
